import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Month picker component
const MonthPickerDropdown = ({ 
  currentMonth, 
  currentYear, 
  onMonthSelect, 
  onClose,
  minSelectableMonth = 0 
}: {
  currentMonth: number;
  currentYear: number;
  onMonthSelect: (month: number) => void;
  onClose: () => void;
  minSelectableMonth?: number;
}) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const today = new Date();
  const isCurrentYear = currentYear === today.getFullYear();

  return (
    <div className="absolute top-full left-1/2 transform -translate-x-1/2 bg-background border border-border rounded-lg shadow-lg z-50 min-w-[240px] mt-2">
      <div className="flex items-center justify-between p-3 border-b border-border">
        <span className="font-medium text-sm">Select Month</span>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        {months.map((month, index) => {
          const isDisabled = isCurrentYear && index < minSelectableMonth;
          const isCurrent = index === currentMonth;
          
          return (
            <Button
              key={month}
              variant={isCurrent ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-8 text-xs",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => !isDisabled && onMonthSelect(index)}
              disabled={isDisabled}
            >
              {month.slice(0, 3)}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

// Year picker component
const YearPickerDropdown = ({ 
  currentYear, 
  onYearSelect, 
  onClose,
  yearRange = { minYear: new Date().getFullYear(), maxYear: new Date().getFullYear() + 20 }
}: {
  currentYear: number;
  onYearSelect: (year: number) => void;
  onClose: () => void;
  yearRange?: { minYear: number; maxYear: number };
}) => {
  const years = [];
  for (let year = yearRange.minYear; year <= yearRange.maxYear; year++) {
    years.push(year);
  }

  return (
    <div className="absolute top-full left-1/2 transform -translate-x-1/2 bg-background border border-border rounded-lg shadow-lg z-50 min-w-[200px] mt-2 max-h-64 overflow-y-auto">
      <div className="flex items-center justify-between p-3 border-b border-border sticky top-0 bg-background">
        <span className="font-medium text-sm">Select Year</span>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        {years.map((year) => {
          const isCurrent = year === currentYear;
          
          return (
            <Button
              key={year}
              variant={isCurrent ? "default" : "ghost"}
              size="sm"
              className="h-8 text-xs"
              onClick={() => onYearSelect(year)}
            >
              {year}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

// Date validation hook
const useDateValidation = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const isValidDate = (date: Date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  };
  
  const isPastDate = (date: Date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };
  
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };
  
  return {
    isValidDate,
    isPastDate,
    isSameDay,
    today
  };
};

// Validation feedback component
const DateValidationFeedback = ({ 
  selectedDate, 
  showError 
}: {
  selectedDate?: Date;
  showError: boolean;
}) => {
  const { isPastDate } = useDateValidation();
  
  if (!selectedDate || !showError || !isPastDate(selectedDate)) {
    return null;
  }
  
  return (
    <div className="bg-warning/10 border border-warning/20 rounded-md p-3 mt-3 flex gap-2">
      <div className="text-warning">⚠️</div>
      <div>
        <div className="text-sm font-medium text-warning">
          Please select a future date for your business exit timeline.
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Your business exit timeline should be set for a future date when you plan to step away from the business.
        </div>
      </div>
    </div>
  );
};

// Enhanced calendar header
const CalendarHeader = ({ 
  currentMonth, 
  currentYear, 
  onMonthClick, 
  onYearClick, 
  onPrevMonth, 
  onNextMonth 
}: {
  currentMonth: number;
  currentYear: number;
  onMonthClick: () => void;
  onYearClick: () => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="flex justify-center items-center py-2 relative">
      <Button 
        variant="outline"
        size="sm"
        onClick={onPrevMonth}
        className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      <div className="flex items-center gap-1">
        <Button 
          variant="ghost"
          size="sm"
          onClick={onMonthClick}
          className="text-sm font-medium hover:bg-accent px-2 py-1 h-auto"
          title="Click to select month"
        >
          {monthNames[currentMonth]}
        </Button>
        
        <Button 
          variant="ghost"
          size="sm"
          onClick={onYearClick}
          className="text-sm font-medium hover:bg-accent px-2 py-1 h-auto"
          title="Click to select year"
        >
          {currentYear}
        </Button>
      </div>
      
      <Button 
        variant="outline"
        size="sm"
        onClick={onNextMonth}
        className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Main enhanced calendar component
export const EnhancedTimelineCalendar = ({ 
  selectedDate, 
  onDateSelect 
}: {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
}) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate?.getMonth() || new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(selectedDate?.getFullYear() || new Date().getFullYear());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [validationError, setValidationError] = useState(false);
  
  const { isValidDate, today } = useDateValidation();
  
  // Ensure we start with at least current month/year
  useEffect(() => {
    const now = new Date();
    if (currentYear < now.getFullYear() || 
        (currentYear === now.getFullYear() && currentMonth < now.getMonth())) {
      setCurrentMonth(now.getMonth());
      setCurrentYear(now.getFullYear());
    }
  }, [currentMonth, currentYear]);
  
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    
    if (isValidDate(date)) {
      setValidationError(false);
      onDateSelect(date);
    } else {
      setValidationError(true);
      setTimeout(() => setValidationError(false), 3000);
    }
  };
  
  const handleMonthSelect = (month: number) => {
    const today = new Date();
    // Ensure we don't select past months in current year
    if (currentYear === today.getFullYear() && month < today.getMonth()) {
      month = today.getMonth();
    }
    setCurrentMonth(month);
    setShowMonthPicker(false);
  };
  
  const handleYearSelect = (year: number) => {
    const minYear = new Date().getFullYear();
    const maxYear = minYear + 25; // 25-year planning horizon
    
    if (year < minYear) year = minYear;
    if (year > maxYear) year = maxYear;
    
    setCurrentYear(year);
    setShowYearPicker(false);
    
    // If we're in current year and current month is in the past, move to current month
    if (year === minYear && currentMonth < new Date().getMonth()) {
      setCurrentMonth(new Date().getMonth());
    }
  };
  
  const navigateMonth = (direction: number) => {
    const newDate = new Date(currentYear, currentMonth + direction);
    const today = new Date();
    
    // Prevent navigation to past months
    if (newDate.getFullYear() < today.getFullYear() || 
        (newDate.getFullYear() === today.getFullYear() && newDate.getMonth() < today.getMonth())) {
      return;
    }
    
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
  };
  
  const yearRange = {
    minYear: new Date().getFullYear(),
    maxYear: new Date().getFullYear() + 25
  };
  
  return (
    <div className="relative">
      <div className="p-3 border border-border rounded-lg bg-background">
        <div className="text-xs text-muted-foreground text-center mb-2">
          Business Exit Timeline Planning
        </div>
        
        <div className="relative">
          <CalendarHeader
            currentMonth={currentMonth}
            currentYear={currentYear}
            onMonthClick={() => setShowMonthPicker(true)}
            onYearClick={() => setShowYearPicker(true)}
            onPrevMonth={() => navigateMonth(-1)}
            onNextMonth={() => navigateMonth(1)}
          />
          
          {showMonthPicker && (
            <MonthPickerDropdown
              currentMonth={currentMonth}
              currentYear={currentYear}
              onMonthSelect={handleMonthSelect}
              onClose={() => setShowMonthPicker(false)}
              minSelectableMonth={currentYear === new Date().getFullYear() ? new Date().getMonth() : 0}
            />
          )}
          
          {showYearPicker && (
            <YearPickerDropdown
              currentYear={currentYear}
              onYearSelect={handleYearSelect}
              onClose={() => setShowYearPicker(false)}
              yearRange={yearRange}
            />
          )}
        </div>
        
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          disabled={(date) => date < today}
          month={new Date(currentYear, currentMonth)}
          showOutsideDays={true}
          className={cn("pointer-events-auto")}
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "hidden", // Hide default caption since we have custom header
            nav: "hidden", // Hide default nav since we have custom header
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground rounded-md",
            day_range_end: "day-range-end",
            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
            day_today: "bg-accent text-accent-foreground font-semibold",
            day_outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
            day_disabled: "text-muted-foreground opacity-30 cursor-not-allowed",
            day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
            day_hidden: "invisible",
          }}
        />
        
        <DateValidationFeedback 
          selectedDate={selectedDate}
          showError={validationError}
        />
      </div>
    </div>
  );
};